import { Form } from 'react-bootstrap';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomDialog from '../../dialogs/CustomDialog';
import {
  useGetApiBookmarkFoldersQuery,
  usePostApiBookmarkFoldersMutation,
} from '../../../redux/store/api/api';
import Loader from '../../loader/Loader';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { addMessage } from '../../../redux/store/layout/slice';
import {
  addExpandedBookmarkFolderId,
  selectContextBookmarkFolder,
} from '../../../redux/store/content/slice';

interface IAddBookmarkFolderDialogProps {
  dialogShow: boolean;
  setDialogShow: (show: boolean) => void;
  setLastUpdatedItem: (updatedId: string) => void;
}

function AddBookmarkFolderDialog({
  setLastUpdatedItem,
  dialogShow,
  setDialogShow,
}: IAddBookmarkFolderDialogProps): JSX.Element {
  const dispatch = useAppDispatch();
  const { t: translation } = useTranslation();
  const parentFolderId = useAppSelector(selectContextBookmarkFolder).id || '';
  const [addBookmarkFolder, { isLoading, isError, error }] =
    usePostApiBookmarkFoldersMutation();
  const [folderName, setFolderName] = useState('');
  const [bookmarkFolderIsValid, setBookmarkFolderIsValid] = useState(true);
  const { refetch } = useGetApiBookmarkFoldersQuery();

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          id: 'AddBookmarkFolderError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [isError]);

  const handleAddBookmarkFolder = () => {
    if (folderName.trim().length === 0) {
      setBookmarkFolderIsValid(false);
      return;
    }

    addBookmarkFolder({
      bookmarkFolder: { parentId: parentFolderId, name: folderName.trim() },
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'AddBookmarkFolderSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        setDialogShow(false);
        setFolderName('');
        refetch();
        dispatch(addExpandedBookmarkFolderId(parentFolderId));
        setLastUpdatedItem(result.resultObject?.id ?? '');
      });
  };

  return (
    <CustomDialog
      titleId='AddBookmarkFolderDialog'
      show={dialogShow}
      closeFunction={() => {
        setFolderName('');
        setBookmarkFolderIsValid(true);
        setDialogShow(false);
      }}
      closeTitle={translation('cancel')}
      actionFunction={handleAddBookmarkFolder}
      actionTitle={translation('add')}
      actionButtonDisabled={isLoading}
      dialogTitle={translation('addBookmarkFolder')}>
      <>
        {isLoading && <Loader />}
        {!isLoading && (
          <>
            <p>{translation('fieldsAreRequiredLegend')}</p>
            <Form.Group className='mb-3' controlId='BookmarkFolderName'>
              <Form.Label>{translation('name')}*</Form.Label>
              <Form.Control
                aria-describedby={
                  !bookmarkFolderIsValid
                    ? 'BookmarkFolderNameInputError'
                    : undefined
                }
                isInvalid={!bookmarkFolderIsValid}
                type='text'
                onChange={(e) => {
                  setFolderName(e.target.value);
                  if (e.target.value.trim().length > 0) {
                    setBookmarkFolderIsValid(true);
                  }
                }}
              />
              <Form.Control.Feedback
                id='BookmarkFolderNameInputError'
                type='invalid'>
                {translation('fieldNotEmpty')}
              </Form.Control.Feedback>
            </Form.Group>
          </>
        )}
      </>
    </CustomDialog>
  );
}

export default AddBookmarkFolderDialog;
